import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, InputNumber, DatePicker, Select, Button, Row, Col, Typography, Space } from "antd";
import { toast } from "../../components/toasts/toastStore";
import SuccessOverlay from "../../components/feedback/SuccessOverlay";
import { apiCargosInstance, authHeaderCargos } from "../../redux/actions/cargos/cargos";
import { apiServiciosInstance, authHeaderServicios } from "../../redux/actions/serviciosPrecios/serviciosPrecios";

import "./PacientesPage.css";

const { Title, Text } = Typography;

export default function CrearCargoPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState({ show: false, to: "/app/cargos" });

  const [pacientes, setPacientes] = useState([]);
  const [buscandoPacientes, setBuscandoPacientes] = useState(false);
  const [servicios, setServicios] = useState([]);
  const busquedaTimer = useRef(null);

  const cargarPacientes = (search) => {
    setBuscandoPacientes(true);
    apiCargosInstance
      .get("/pacientes", { headers: authHeaderCargos(), params: search ? { search } : {} })
      .then((res) => setPacientes(Array.isArray(res.data) ? res.data : res.data?.items ?? []))
      .catch(() => setPacientes([]))
      .finally(() => setBuscandoPacientes(false));
  };

  useEffect(() => {
    cargarPacientes("");
    apiServiciosInstance
      .get("/servicios-precios", { headers: authHeaderServicios() })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
        setServicios(data.filter((s) => s.active !== false));
      })
      .catch(() => {});
    return () => clearTimeout(busquedaTimer.current);
  }, []);

  const buscarPacientes = (value) => {
    clearTimeout(busquedaTimer.current);
    busquedaTimer.current = setTimeout(() => cargarPacientes((value || "").trim()), 300);
  };

  const handleServicioChange = (idServicio) => {
    const servicio = servicios.find((s) => s.id_servicio === idServicio);
    if (!servicio) return;
    const actual = form.getFieldsValue(["concepto", "monto_estimado"]);
    form.setFieldsValue({
      concepto: actual.concepto || servicio.nombre,
      monto_estimado: actual.monto_estimado ?? Number(servicio.precio_lista),
    });
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload = {
      id_paciente: values.id_paciente,
      id_servicio: values.id_servicio || undefined,
      concepto: values.concepto.trim(),
      monto_estimado: values.monto_estimado ?? undefined,
      monto_final: values.monto_final ?? undefined,
      fecha: values.fecha ? values.fecha.format("YYYY-MM-DD") : undefined,
    };

    setSaving(true);
    try {
      const { data } = await apiCargosInstance.post("/cargos", payload, { headers: authHeaderCargos() });
      const newId = data?.id ?? data?.id_cargo ?? data?.item?.id_cargo ?? data?.item?.id;
      setSuccess({ show: true, to: newId ? `/app/cargos/${newId}` : "/app/cargos" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "No se pudo crear el cargo");
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="paq-title" style={{ marginBottom: 0 }}>Nuevo cargo</Title>
            <Text className="paq-subtitle">Registra un cargo a un paciente y, si aplica, vincúlalo a un servicio del catálogo.</Text>
          </Space>

          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate("/app/cargos")} disabled={saving || success.show}>
              Cancelar
            </Button>
            <Button
              type="primary"
              loading={saving}
              disabled={success.show}
              icon={<i className="fa-solid fa-plus" />}
              onClick={handleSave}
              style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
            >
              Crear cargo
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical">
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-file-invoice-dollar" /></span>
              Datos del cargo
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="id_paciente"
                  label={<span className="paq-field-label">Paciente</span>}
                  rules={[{ required: true, message: "Selecciona un paciente" }]}
                >
                  <Select
                    showSearch
                    placeholder="Busca por nombre o teléfono..."
                    filterOption={false}
                    loading={buscandoPacientes}
                    onSearch={buscarPacientes}
                    options={pacientes.map((p) => ({
                      value: p.id_paciente ?? p.id,
                      label: `${p.nombre_completo || p.nombre || `Paciente #${p.id_paciente ?? p.id}`}${p.telefono ? " · " + p.telefono : ""}`,
                    }))}
                    notFoundContent={buscandoPacientes ? "Buscando..." : "Sin resultados"}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="id_servicio" label={<span className="paq-field-label">Servicio (opcional)</span>}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Selecciona un servicio del catálogo"
                    onChange={handleServicioChange}
                    options={servicios.map((s) => ({
                      value: s.id_servicio,
                      label: `${s.nombre} — $${Number(s.precio_lista || 0).toFixed(2)}`,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="concepto"
                  label={<span className="paq-field-label">Concepto</span>}
                  rules={[{ required: true, whitespace: true, message: "Requerido" }]}
                >
                  <Input placeholder="Ej. Consulta oftalmológica" maxLength={200} autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="fecha" label={<span className="paq-field-label">Fecha (opcional)</span>}>
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" placeholder="Selecciona la fecha" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="monto_estimado" label={<span className="paq-field-label">Monto estimado</span>}>
                  <InputNumber style={{ width: "100%" }} min={0} precision={2} addonBefore="$" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="monto_final" label={<span className="paq-field-label">Monto final (opcional)</span>}>
                  <InputNumber style={{ width: "100%" }} min={0} precision={2} addonBefore="$" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>

      <SuccessOverlay
        show={success.show}
        title="Cargo creado"
        subtitle="Se registró correctamente."
        onDone={() => navigate(success.to)}
      />
    </main>
  );
}
